import type { TakiSdk } from '@taki/sdk';

const CANAUX = [
  { key: 'general', nom: 'Général', description: 'Démo Canaux : annonces et échanges de l’équipe.', prive: false },
  { key: 'produit', nom: 'Produit', description: 'Démo Canaux : décisions, maquettes et retours.', prive: false },
  { key: 'operations', nom: 'Opérations', description: 'Démo Canaux : coordination des essais et du support.', prive: false },
  { key: 'coordination', nom: 'Coordination privée', description: 'Démo Canaux : échange réservé aux participants.', prive: true },
] as const;
const MESSAGES = [
  { key: 'accueil', canal: 'general', texte: 'Bienvenue ! Ce canal rassemble les annonces utiles à toute l’équipe.' },
  { key: 'accueil-reponse', canal: 'general', parent: 'accueil', texte: 'On peut répondre dans un fil sans quitter le canal.' },
  { key: 'usage', canal: 'general', texte: 'Ouvrez un fil pour suivre un point précis. Les nouveaux messages restent visibles dans le canal.' },
  { key: 'maquette', canal: 'produit', texte: 'La nouvelle vue regroupe les canaux et les messages.' },
  { key: 'maquette-reponse', canal: 'produit', parent: 'maquette', texte: 'Le panneau de détails laisse davantage de place à la conversation.' },
  { key: 'essais', canal: 'operations', texte: '@tous Les essais internes commencent cette semaine. Notez les points qui surprennent.' },
  { key: 'essais-reponse', canal: 'operations', parent: 'essais', texte: 'Je rassemble les retours dans ce fil.' },
  { key: 'prive', canal: 'coordination', texte: 'Cet échange est réservé aux personnes de ce canal.' },
  { key: 'direct', canal: 'direct', texte: 'Bonjour, voici un exemple de message direct.' },
] as const;

type PortDemo = Pick<TakiSdk, 'conversations' | 'messages' | 'personnes'>;
type ConversationDemo = Awaited<ReturnType<PortDemo['conversations']['list']>>[number];
type MessageDemo = Awaited<ReturnType<PortDemo['messages']['envoyer']>>;

export interface ResultatDemoCanaux {
  created: number;
  skipped: number;
  canalId: string;
  message: string;
}

/** Convertit uniquement les jetons historiques connus. Un prénom ambigu reste du texte. */
export function migrerTexteDemo(texte: string, personnes: Array<{ personId: string; displayName: string }>): string {
  const parPrenom = new Map<string, string | null>();
  for (const personne of personnes) {
    const prenom = personne.displayName.trim().split(/\s+/u)[0]?.toLocaleLowerCase('fr');
    if (!prenom) continue;
    parPrenom.set(prenom, parPrenom.has(prenom) ? null : personne.personId);
  }
  return texte.replace(/@canal\b|@([\p{L}\p{M}]+)\b/giu, (jeton, prenom: string | undefined) => {
    if (jeton.toLocaleLowerCase('fr') === '@canal') return '@tous';
    if (jeton.toLocaleLowerCase('fr') === '@lia') return '@Lia';
    const id = parPrenom.get((prenom ?? '').toLocaleLowerCase('fr'));
    return id ? `@[${id}]` : jeton;
  });
}

function doublon(label: string): never { throw new Error(`${label} existe en double. Import interrompu.`); }
function nomComparable(nom: string): string { return nom.trim().replace(/\s+/gu, ' ').toLocaleLowerCase('fr'); }

/** Lit les pages accessibles. La liste courte du SDK ne suffit pas pour retrouver une démo ancienne. */
async function canauxAccessibles(sdk: PortDemo, verifierSession: () => void): Promise<ConversationDemo[]> {
  const canaux: ConversationDemo[] = [];
  for (const acces of ['equipe', 'prive'] as const) {
    let after: string | undefined;
    while (true) {
      verifierSession();
      const page = await sdk.conversations.list({ type: 'canal', acces, orderBy: { field: 'name', dir: 'asc' }, limit: 100,
        ...(after ? { after } : {}) });
      verifierSession();
      canaux.push(...page);
      if (page.length < 100) break;
      after = page[page.length - 1]!.id;
    }
  }
  return canaux;
}
function memeCanal(canal: ConversationDemo, attendu: typeof CANAUX[number]): boolean {
  return canal.type === 'canal' && canal.name === attendu.nom && canal.description === attendu.description &&
    (canal.acces?.visibilite === 'prive') === attendu.prive;
}

/** Import explicite et reprenable. Le SDK pose l'auteur réel et les mentions. */
export async function importerDemoCanaux(
  sdk: PortDemo,
  config: { orgId: string; local: boolean },
  acteurPersonId: string,
  verifierSession: () => void = () => {},
): Promise<ResultatDemoCanaux> {
  if (!config.local) throw new Error('Le jeu de démonstration est réservé aux émulateurs locaux.');
  if (!acteurPersonId.trim()) throw new Error('Connectez un compte local avant d’importer le jeu de démonstration.');
  verifierSession();
  const personnes = await sdk.personnes.list();
  const moi = personnes.find((personne) => personne.personId === acteurPersonId);
  if (!moi) throw new Error('Votre personne est absente de l’annuaire de cette organisation.');
  const autre = personnes.find((personne) => personne.personId !== moi.personId && personne.statut === 'actif');
  if (!autre) throw new Error('La démonstration privée exige une autre personne active dans l’annuaire.');
  verifierSession();

  let created = 0;
  let skipped = 0;
  const ids = new Map<string, string>();
  const canaux = await canauxAccessibles(sdk, verifierSession);
  for (const attendu of CANAUX) {
    verifierSession();
    const homonymes = canaux.filter((canal) => canal.type === 'canal' && nomComparable(canal.name) === nomComparable(attendu.nom));
    if (homonymes.length > 1) doublon(`Le canal ${attendu.nom}`);
    if (homonymes.length && !memeCanal(homonymes[0]!, attendu)) throw new Error(`Le canal ${attendu.nom} existe déjà avec un autre accès ou une autre description.`);
    if (homonymes.length) {
      const canal = homonymes[0]!;
      if (attendu.prive && (canal.acces?.visibilite !== 'prive' || !canal.acces.participants.includes(autre.personId))) {
        throw new Error('Le canal privé de démonstration existe avec d’autres participants.');
      }
      ids.set(attendu.key, canal.id);
      skipped += 1;
      continue;
    }
    verifierSession();
    const cree = await sdk.conversations.creerCanal({ nom: attendu.nom, description: attendu.description, prive: attendu.prive,
      ...(attendu.prive ? { participants: [autre.personId] } : {}) });
    ids.set(attendu.key, cree.id);
    created += 1;
  }
  verifierSession();
  const directsExistants = await sdk.conversations.listerDirects();
  verifierSession();
  const direct = await sdk.conversations.creerDirect([autre.personId]);
  ids.set('direct', direct.id);
  if (directsExistants.some((item) => item.id === direct.id)) skipped += 1;
  else created += 1;

  const messages = new Map<string, string>();
  for (const attendu of MESSAGES) {
    verifierSession();
    const conversationId = ids.get(attendu.canal);
    if (!conversationId) throw new Error(`Conversation de démonstration introuvable : ${attendu.canal}.`);
    const parentId = 'parent' in attendu ? messages.get(attendu.parent) : undefined;
    if ('parent' in attendu && !parentId) throw new Error(`Racine de démonstration introuvable : ${attendu.parent}.`);
    const texte = migrerTexteDemo(attendu.texte, personnes);
    const existants = await sdk.messages.list({ type: 'conversation', acces: { conversation: conversationId }, where: { texte }, limit: 2 });
    if (existants.length > 1) doublon(`Le message ${attendu.key}`);
    const existant = existants[0];
    if (existant && (existant.conversationId !== conversationId || existant.parentId !== parentId || existant.texte !== texte)) {
      throw new Error(`Le message ${attendu.key} existe déjà avec un autre fil.`);
    }
    if (existant) {
      messages.set(attendu.key, existant.id);
      skipped += 1;
      continue;
    }
    verifierSession();
    const cree: MessageDemo = await sdk.messages.envoyer(conversationId, texte, parentId ? { parentId } : undefined);
    messages.set(attendu.key, cree.id);
    created += 1;
  }
  const canalId = ids.get('general');
  if (!canalId) throw new Error('Canal Général introuvable après import.');
  return { created, skipped, canalId,
    message: created === 0 ? 'Le jeu de démonstration est déjà en place.' : `${created} éléments créés, ${skipped} déjà présents.` };
}
